import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

import * as FaIcons from 'react-icons/fa';
import * as HiIcons from 'react-icons/hi';

const {
  FaHome,
  FaNewspaper,
      FaProjectDiagram,
      FaChalkboardTeacher,
  FaUsers,
  FaCalendarAlt,
  FaUserTie ,
  FaUserCircle,
  FaSignOutAlt,
  FaUserPlus,
  FaSignInAlt,
  FaBars,
  FaTimes,
  FaUserShield,
} = FaIcons as any;

const {
  HiOutlineMenuAlt3,
  HiOutlineX,
  HiOutlineUser,
  HiOutlineLogout
} = HiIcons as any;

interface NavItem {
  name: string;
  path: string;
  icon?: React.ReactNode;
}

interface UserProfile {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  photoURL?: string;
  avatar?: string;
}

const Header: React.FC = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // Updated navItems with requested changes
  const navItems: NavItem[] = [
    { 
      name: 'Home', 
      path: '/', 
      icon: <FaHome className="text-sm" />
    },
    { 
      name: 'Activities', 
      path: '/activities', 
      icon: <FaNewspaper className="text-sm" />
    },
    {
      name: 'Programs',
      path: '/programs',
      icon: <FaProjectDiagram className="text-sm" />
    },
    { 
      name: 'Leadership', 
      path: '/leadership', 
      icon: <FaUserTie  className="text-sm" />
    },
    { 
      name: 'Team', 
      path: '/team', 
      icon: <FaCalendarAlt className="text-sm" />
    },
    { 
      name: 'About', 
      path: '/about', 
      icon: <FaUsers className="text-sm" />
    },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
        toast.success('Signed out successfully');
        navigate('/');
      }
    } catch (error: any) {
      toast.error('Logout failed');
    }
  };

  const typedUserProfile = userProfile as UserProfile | null;
  const avatarUrl =
    typedUserProfile?.photoURL ||
    (currentUser as any)?.photoURL ||
    (typedUserProfile as any)?.avatar ||
    undefined;

  return (
    <>
      {/* Main Header */}
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-lg border-b border-gray-100' 
          : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2 group">
                  <motion.div 
                  className="relative"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="relative w-14 h-14 rounded-full flex items-center justify-center">
                    <svg
                      viewBox="0 0 100 100"
                      className="absolute inset-0 text-slate-700"
                      aria-hidden="true"
                    >
                      <defs>
                        <path
                          id="logoCircleTop"
                          d="M 50,50 m -36,0 a 36,36 0 1,1 72,0"
                        />
                        <path
                          id="logoCircleBottom"
                          d="M 50,50 m -40,0 a 40,40 0 1,0 80,0"
                        />
                      </defs>
                      <text
                        fontSize="6"
                        fontWeight="600"
                        letterSpacing="1.6"
                        fill="currentColor"
                      >
                        <textPath href="#logoCircleTop" startOffset="50%" textAnchor="middle">
                          ALHAJI INUSAH FOUNDATION
                        </textPath>
                      </text>
                      <text
                        fontSize="6"
                        fontWeight="600"
                        letterSpacing="1.6"
                        fill="currentColor"
                      >
                        <textPath href="#logoCircleBottom" startOffset="50%" textAnchor="middle">
                          KNOWLEDGE IS POWER
                        </textPath>
                      </text>
                    </svg>
                    <img
                      src="/LogoAifcc.png"
                      alt="AIFCC Logo"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                </motion.div>
                <div className="hidden md:block">
                  <h1 className="text-xl font-bold">
                    <span className="text-slate-900">ALHAJI</span>{" "}
                    <span className="bg-gradient-to-r from-indigo-900 to-purple-800 bg-clip-text text-transparent">INUSAH</span>
                    <span className="block text-xs font-medium tracking-[0.7em] text-slate-500">
                      FOUNDATION
                    </span>
                  </h1>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 group ${
                      location.pathname === item.path
                        ? 'text-indigo-700 bg-indigo-50'
                        : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {item.icon}
                    <span className="font-medium text-sm">{item.name}</span>
                  </Link>
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Auth Section */}
              {currentUser ? (
                <div className="flex items-center gap-2">
                  {/* User Avatar */}
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-2"
                    >
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="User avatar"
                          className="w-10 h-10 rounded-full object-cover shadow-sm"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold shadow-sm">
                          {typedUserProfile?.firstName?.charAt(0) || 'U'}
                        </div>
                      )}
                    </button>

                    {/* User Menu Dropdown */}
                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50"
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        >
                          {/* User Info */}
                          <div className="px-4 py-3 border-b border-gray-100">
                            <div className="font-semibold text-slate-900">
                              {typedUserProfile?.firstName} {typedUserProfile?.lastName}
                            </div>
                            <div className="text-sm text-slate-500 truncate">
                              {typedUserProfile?.email}
                            </div>
                            <div className="text-xs bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent font-medium mt-1">
                              {typedUserProfile?.role?.replace('_', ' ').toUpperCase() || 'MEMBER'}
                            </div>
                          </div>

                          {/* Menu Items */}
                          <div className="py-2">
                            <Link
                              to="/user/profile"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:text-indigo-700 hover:bg-slate-50 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <HiOutlineUser className="text-lg" />
                              <span>My Profile</span>
                            </Link>

                            {typedUserProfile?.role?.includes('admin') && (
                              <Link
                                to="/admin/library"
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-amber-700 hover:text-amber-800 hover:bg-amber-50 transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <FaUserShield />
                                <span>Admin Dashboard</span>
                              </Link>
                            )}
                          </div>

                          {/* Logout */}
                          <div className="border-t border-gray-100 pt-2">
                            <button
                              onClick={() => {
                                handleLogout();
                                setIsUserMenuOpen(false);
                              }}
                              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                            >
                              <HiOutlineLogout className="text-lg" />
                              <span>Sign Out</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                // Auth buttons side by side in their own container
                <div className="hidden lg:flex items-center gap-0 bg-slate-50 rounded-lg border border-slate-200 p-1">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-indigo-700 rounded-md hover:bg-slate-50 transition-colors"
                    >
                      Sign In
                    </Link>
                  <div className="h-4 w-px bg-slate-300"></div>
                  <Link
                    to="/register"
                    className="px-4 py-2 text-sm font-medium text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
                  >
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-slate-700 hover:text-indigo-700 rounded-lg hover:bg-slate-50"
              >
                {isMobileMenuOpen ? (
                  <HiOutlineX className="text-2xl" />
                ) : (
                  <HiOutlineMenuAlt3 className="text-2xl" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 lg:hidden"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 30 }}
            >
              <div className="h-full flex flex-col">
                {/* Menu Header */}
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2">
                      <div className="relative w-14 h-14 rounded-full flex items-center justify-center">
                        <svg
                          viewBox="0 0 100 100"
                          className="absolute inset-0 text-slate-700"
                          aria-hidden="true"
                        >
                          <defs>
                            <path
                            id="logoCircleTopMobile"
                            d="M 50,50 m -36,0 a 36,36 0 1,1 72,0"
                          />
                          <path
                            id="logoCircleBottomMobile"
                            d="M 50,50 m -40,0 a 40,40 0 1,0 80,0"
                          />
                          </defs>
                          <text
                            fontSize="6"
                            fontWeight="600"
                            letterSpacing="1.6"
                            fill="currentColor"
                          >
                            <textPath href="#logoCircleTopMobile" startOffset="50%" textAnchor="middle">
                              ALHAJI INUSAH FOUNDATION
                            </textPath>
                          </text>
                          <text
                            fontSize="6"
                            fontWeight="600"
                            letterSpacing="1.6"
                            fill="currentColor"
                          >
                            <textPath href="#logoCircleBottomMobile" startOffset="50%" textAnchor="middle">
                              KNOWLEDGE IS POWER
                            </textPath>
                          </text>
                        </svg>
                        <img
                          src="/LogoAifcc.png"
                          alt="AIFCC Logo"
                          className="w-9 h-9 object-contain"
                        />
                      </div>
                      <div>
                        <div className="font-bold">
                          <span className="block text-slate-900">ALHAJI</span>
                          <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">INUSAH</span>
                          <span className="block text-sm font-semibold text-slate-600">FOUNDATION</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 text-slate-500 hover:text-slate-700"
                    >
                      <HiOutlineX className="text-2xl" />
                    </button>
                  </div>

                  {/* Auth Buttons for Mobile (when not logged in) */}
                  {!currentUser && (
                    <div className="flex gap-3">
                      <Link
                        to="/login"
                        className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg text-center"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/register"
                        className="flex-1 px-4 py-3 text-indigo-600 text-sm font-medium rounded-lg text-center hover:bg-indigo-50 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </div>
                  )}
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto py-4">
                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <div key={item.name} className="px-4">
                        <Link
                          to={item.path}
                          className={`flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm font-medium ${
                              location.pathname === item.path
                                ? 'bg-indigo-50 text-indigo-700'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.icon}
                          <span>{item.name}</span>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Section */}
                {currentUser && (
                  <div className="border-t border-slate-100 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="User avatar"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {typedUserProfile?.firstName?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-slate-900">
                          {typedUserProfile?.firstName} {typedUserProfile?.lastName}
                        </div>
                        <div className="text-sm text-slate-500">
                          {typedUserProfile?.email}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Link
                        to="/user/profile"
                        className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        My Profile
                      </Link>
                      {typedUserProfile?.role?.includes('admin') && (
                        <Link
                          to="/admin/library"
                          className="block px-4 py-3 text-sm text-amber-700 hover:amber-50 rounded-lg"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="block w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg text-left"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
